import { OperationVariables } from '@apollo/client';
import { keyframes } from '@emotion/react';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faCaretDown,
  faCaretRight,
  faCaretUp,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Checkbox,
  Collapse,
  IconButton,
  Table as MuiTable,
  type TableProps as MuiTableProps,
  Paper,
  Skeleton,
  styled,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  useMediaQuery,
} from '@mui/material';
import {
  CellContext,
  ColumnDefTemplate,
  createColumnHelper,
  type ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  Header,
  type OnChangeFn,
  type Row,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { concat, join, map } from 'lodash';
import {
  Fragment,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { TablePropsBase, TBreakpoints } from './table-types';

// This type is for casting due to original ColumnMeta interface issue.
interface TableColumnMeta<TData, TValue> {
  className?: string | string[];
  minimumBreakpoint?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  editor?: ColumnDefTemplate<CellContext<TData, TValue>>;
}

// Staggered entrance for result rows. Each row fades and lifts into place, with
// a per-row delay so they cascade in one after another.
const rowFadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;
// Delay between consecutive rows, capped so long pages still finish quickly.
const ROW_STAGGER_MS = 35;
const ROW_STAGGER_MAX_INDEX = 20;

// Shared row height for expandable list tables — clears the tallest inline
// control (the 32px expander/action buttons plus cell padding) so text-only
// rows don't sit shorter than rows with inline inputs.
const EXPANDABLE_ROW_HEIGHT = 52;

const StripedTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(even)': {
    background: `linear-gradient(0deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1))`,
    backdropFilter: 'brightness(0.97)',
  },
})) as any;

const StyledTableCell = styled(TableCell)(() => ({
  color: 'inherit',
  // A positioning context, so an in-cell editor can lay itself over the whole
  // cell (padding included) instead of only its content box.
  position: 'relative',
})) as any;

export interface TableProps<TData, TQuery, TVars extends OperationVariables>
  extends TablePropsBase<TData, TQuery, TVars> {
  rows: TData[];
  onSelect?: (row: TData) => void;
  props?: Partial<MuiTableProps>;
  striped?: boolean;
  /**
   * Drop the header row entirely.
   *
   * For a table whose single column renders a card: there are no columns to
   * align, so the header is a label over nothing — and with the rows drawn
   * as cards it reads as a stray heading above them.
   */
  hideHeader?: boolean;
  loading?: boolean;
  loadingRows?: number;
  rowAlignment?: 'top' | 'middle' | 'bottom';
  /** When true, cells use white-space: nowrap to keep content on a single line */
  nowrap?: boolean;
  /**
   * When true, result rows animate in with a staggered fade/lift on mount and
   * whenever the row set changes. Respects `prefers-reduced-motion`. Opt-in so
   * only list/search surfaces animate, not every embedded table.
   */
  animateRows?: boolean;
  /** When true, render a leading checkbox column for row multi-selection. */
  selectable?: boolean;
  /** Controlled set of selected row ids (see `getRowId`). */
  selectedIds?: Set<string>;
  /** Toggle a single row's selection. */
  onToggleRowSelection?: (id: string) => void;
  /** Toggle selection of all currently rendered rows (passes their ids). */
  onToggleAllSelection?: (ids: string[]) => void;
  /** Derive a row's stable id for selection; defaults to `row._id`. */
  getRowId?: (row: TData) => string;
  /** When provided, only rows returning true get a selection checkbox. */
  isRowSelectable?: (row: TData) => boolean;
  /**
   * Accordion expansion: opening a row closes whichever was open. Suits panels
   * heavy enough that several at once turn the table into a wall of detail.
   */
  singleExpand?: boolean;
}

export function Table<
  TData,
  TQuery = object,
  TVars extends OperationVariables = object,
>({
  columns,
  renderSubComponent,
  rows,
  onSelect,
  props,
  rowAlignment = 'middle',
  nowrap = false,
  isEditModeEnabled = false,
  onRowEdit = () => {},
  striped = true,
  hideHeader = false,
  loading = false,
  loadingRows = 10,
  animateRows = false,
  selectable = false,
  selectedIds,
  onToggleRowSelection,
  onToggleAllSelection,
  getRowId = (row: TData) => (row as { _id?: string })?._id ?? '',
  isRowSelectable,
  singleExpand = false,
}: TableProps<TData, TQuery, TVars>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  // Expansion is controlled only to support `singleExpand`; otherwise this just
  // mirrors what the table would have held internally.
  const handleExpandedChange: OnChangeFn<ExpandedState> = useCallback(
    (updater) => {
      setExpanded((previous) => {
        const next =
          typeof updater === 'function' ? updater(previous) : updater;
        if (!singleExpand || typeof next !== 'object' || next === null) {
          return next;
        }
        const wasOpen = typeof previous === 'object' ? previous : {};
        // Keep only the row this toggle just opened; a toggle that closed a row
        // opens nothing, so the map is left as-is (empty).
        const justOpened = Object.keys(next).find(
          (rowId) => next[rowId] && !wasOpen[rowId],
        );
        return justOpened ? { [justOpened]: true } : next;
      });
    },
    [singleExpand],
  );

  const prefersReducedMotion = useMediaQuery(
    '(prefers-reduced-motion: reduce)',
  );
  const shouldAnimateRows = animateRows && !prefersReducedMotion;

  // TanStack rows here are keyed by index (no getRowId), so a fresh result set
  // reuses the same DOM nodes and the CSS mount animation wouldn't replay.
  // Derive a generation string from the rows' identities and fold it into each
  // row key: when the set genuinely changes (new search, page, sort) the rows
  // remount and the cascade plays again; on unrelated re-renders it stays put.
  const rowsGeneration = useMemo(
    () =>
      shouldAnimateRows
        ? (rows as { _id?: string }[]).map((r, i) => r?._id ?? i).join('|')
        : '',
    [rows, shouldAnimateRows],
  );

  const getBreakpointSx = useCallback((breakpoint?: TBreakpoints) => {
    if (!breakpoint) return {};

    return {
      display: { xs: 'none', [breakpoint]: 'table-cell' },
      '@media print': {
        display: 'table-cell',
      },
    };
  }, []);

  // TanStack only records a column's sizes; nothing renders them unless we do.
  // A column that declares a hard `maxSize` is asking to be a tight column (an
  // icon gutter), so give it that as its width instead of letting auto layout
  // hand it a share of the spare room.
  //
  // A width, not a cap: auto layout treats it as the width to prefer, and still
  // widens the column for a row whose content needs more (a gutter showing two
  // icons). `max-width` on a cell would hold the column there and let that
  // content spill over the next one.
  const getSizeSx = useCallback((maxSize?: number) => {
    // TanStack resolves an unset `maxSize` to MAX_SAFE_INTEGER on some column
    // defs; only a real, declared cap counts.
    if (!maxSize || maxSize >= Number.MAX_SAFE_INTEGER) return {};

    return {
      width: maxSize,
      boxSizing: 'border-box',
      px: 0.5,
    };
  }, []);

  const _columns = useMemo(() => {
    const cols = map(
      columns,
      (
        {
          label,
          field,
          formatter,
          minSize,
          maxSize,
          footer,
          className,
          minimumBreakpoint,
          inlineEditor,
        },
        id,
      ) => {
        const columnHelper = createColumnHelper<TData>();

        const header = () => (
          <div className={join(className || [], ' ')}>{label}</div>
        );

        if (field) {
          return columnHelper.accessor(field, {
            header,
            minSize,
            maxSize,
            id: field.toString(),
            meta: {
              className,
              minimumBreakpoint,
              editor: inlineEditor
                ? (props: any) => {
                    if (inlineEditor) {
                      const initialValue = props.cell.getValue() as string;

                      return (
                        <div className={join(className || [], ' ')}>
                          {inlineEditor({
                            initialValue,
                            row: props.row,
                            column: props.column,
                            updateRow: onRowEdit,
                          })}
                        </div>
                      );
                    }
                  }
                : undefined,
            },
            cell: (props) => (
              <div className={join(className || [], ' ')}>
                {formatter
                  ? formatter(props.row.original, props.row.index + 1)
                  : (props.renderValue() as ReactNode)}
              </div>
            ),
            footer: () => (
              <div className={join(className || [], ' ')}>
                {footer?.formatter
                  ? footer.formatter(footer.aggregation(rows, field))
                  : footer?.aggregation(rows, field)}
              </div>
            ),
          });
        }

        return columnHelper.display({
          minSize,
          maxSize,
          header,
          id: id.toString(),
          cell: (props) => {
            const val = formatter
              ? formatter(props.row.original, props.row.index + 1)
              : props.getValue();

            return (
              <div className={join(className || [], ' ')}>
                {val as ReactNode}
              </div>
            );
          },
        });
      },
    );

    if (renderSubComponent) {
      return concat(
        [
          {
            id: 'expander',
            header: () => null,
            cell: ({ row }: { row: Row<TData> }) => {
              const isExpanded = row.getIsExpanded();
              return row.getCanExpand() ? (
                <IconButton
                  // Expanding a row is not selecting it: without stopping here
                  // the click also reaches the row's `onSelect`, so opening the
                  // sub-panel would navigate away from the table.
                  onClick={(event) => {
                    event.stopPropagation();
                    row.getToggleExpandedHandler()();
                  }}
                  size="small"
                  sx={{ width: 32, height: 32 }}
                >
                  <FontAwesomeIcon
                    icon={isExpanded ? faCaretDown : faCaretRight}
                    color={isExpanded ? 'primary' : 'inherit'}
                  />
                </IconButton>
              ) : null;
            },
          },
        ],
        cols,
      );
    }

    return cols;
  }, [columns, onRowEdit, renderSubComponent, rows]);

  const table = useReactTable<TData>({
    data: rows,
    columns: _columns,
    state: { sorting, expanded },
    enableColumnFilters: true,
    enableSorting: true,
    // Only rows whose sub-component actually renders content are expandable —
    // rows that return null (e.g. already-assigned animals) shouldn't show a
    // caret that opens onto an empty panel.
    getRowCanExpand: (row) =>
      !!renderSubComponent && renderSubComponent(row) != null,
    onSortingChange: setSorting,
    onExpandedChange: handleExpandedChange,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // debugTable: true,
  });

  const renderHeader = useCallback((header: Header<TData, unknown>) => {
    if (!header.isPlaceholder) {
      let icon: IconProp | undefined;

      switch (header.column.getIsSorted()) {
        case 'asc':
          icon = faCaretUp;
          break;
        case 'desc':
          icon = faCaretDown;
          break;
      }

      return (
        <div
          className="d-flex gap-2 text-nowrap"
          style={
            header.column.getCanSort()
              ? { cursor: 'pointer', userSelect: 'none' }
              : {}
          }
          onClick={header.column.getToggleSortingHandler()}
        >
          {flexRender(header.column.columnDef.header, header.getContext())}
          {icon && <FontAwesomeIcon icon={icon} size="lg" />}
        </div>
      );
    }
  }, []);

  const TableRowComponent = (striped ? StripedTableRow : TableRow) as any;

  const renderLoadingRows = useCallback(() => {
    const headerGroups = table.getHeaderGroups();
    const columnCount = headerGroups[0]?.headers.length || 0;

    return Array.from({ length: loadingRows }, (_, index) => (
      <TableRowComponent key={`loading-${index}`} hover>
        {selectable && (
          <StyledTableCell padding="checkbox">
            <Checkbox size="small" disabled />
          </StyledTableCell>
        )}
        {Array.from({ length: columnCount }, (_, cellIndex) => {
          const header = headerGroups[0]?.headers[cellIndex];
          const meta = header?.column.columnDef.meta as TableColumnMeta<
            TData,
            unknown
          >;

          return (
            <StyledTableCell
              key={`loading-cell-${index}-${cellIndex}`}
              sx={getBreakpointSx(meta?.minimumBreakpoint)}
            >
              <Skeleton variant="text" height={40} sx={{ m: -0.5 }} />
            </StyledTableCell>
          );
        })}
      </TableRowComponent>
    ));
  }, [table, loadingRows, getBreakpointSx, TableRowComponent, selectable]);

  const displayFooter = useMemo(
    () => columns.some((col) => col.footer),
    [columns],
  );

  // Selection state derived from the currently rendered (selectable) rows.
  const visibleRowIds = selectable
    ? table
        .getRowModel()
        .rows.filter((r) => !isRowSelectable || isRowSelectable(r.original))
        .map((r) => getRowId(r.original))
    : [];
  const allVisibleSelected =
    !!selectedIds &&
    visibleRowIds.length > 0 &&
    visibleRowIds.every((id) => selectedIds.has(id));
  const someVisibleSelected =
    !!selectedIds &&
    visibleRowIds.some((id) => selectedIds.has(id)) &&
    !allVisibleSelected;

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        backgroundColor: 'inherit',
        color: 'inherit',
        ...(nowrap && {
          '& .MuiTableCell-root': { whiteSpace: 'nowrap' },
        }),
      }}
    >
      <MuiTable
        size="small"
        {...props}
        // sx={{ backgroundColor: 'inherit', ...props?.sx }}
      >
        {!hideHeader && (
          <TableHead>
            {map(table.getHeaderGroups(), (headerGroup) => (
              <TableRow key={headerGroup.id}>
                {selectable && (
                  <StyledTableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allVisibleSelected}
                      indeterminate={someVisibleSelected}
                      onChange={() => onToggleAllSelection?.(visibleRowIds)}
                    />
                  </StyledTableCell>
                )}
                {map(headerGroup.headers, (header) => {
                  const meta = header.column.columnDef.meta as TableColumnMeta<
                    TData,
                    unknown
                  >;
                  return (
                    <StyledTableCell
                      key={header.id}
                      sx={{
                        fontWeight: 'bold',
                        ...getBreakpointSx(meta?.minimumBreakpoint),
                        ...getSizeSx(header.column.columnDef.maxSize),
                      }}
                    >
                      {renderHeader(header)}
                    </StyledTableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
        )}
        <TableBody>
          {loading
            ? renderLoadingRows()
            : table.getRowModel().rows.map((row, idx) => (
                <Fragment
                  key={
                    shouldAnimateRows
                      ? `${rowsGeneration}:${row?.id ?? idx}`
                      : (row?.id ?? idx)
                  }
                >
                  <TableRowComponent
                    hover
                    sx={{
                      verticalAlign: rowAlignment,
                      // Expandable list rows mix tall interactive controls
                      // (inline inputs, expander/delete buttons) with plain-text
                      // rows; a shared floor height keeps every row the same
                      // height regardless of which controls it happens to carry.
                      ...(renderSubComponent && {
                        height: EXPANDABLE_ROW_HEIGHT,
                      }),
                      backgroundColor: row.getIsExpanded()
                        ? 'action.selected'
                        : 'transparent',
                      cursor: onSelect ? 'pointer' : 'default',
                      ...(shouldAnimateRows && {
                        animation: `${rowFadeIn} 0.3s ease both`,
                        animationDelay: `${
                          Math.min(idx, ROW_STAGGER_MAX_INDEX) * ROW_STAGGER_MS
                        }ms`,
                      }),
                    }}
                    data-id={buildRowDataId(row)}
                    onClick={() => onSelect && onSelect(row.original)}
                  >
                    {selectable && (
                      <StyledTableCell
                        padding="checkbox"
                        onClick={(e: MouseEvent) => e.stopPropagation()}
                      >
                        {!isRowSelectable || isRowSelectable(row.original) ? (
                          <Checkbox
                            size="small"
                            checked={
                              !!selectedIds &&
                              selectedIds.has(getRowId(row.original))
                            }
                            onChange={() =>
                              onToggleRowSelection?.(getRowId(row.original))
                            }
                          />
                        ) : (
                          // Keep the column aligned for non-selectable rows by
                          // showing a disabled box rather than an empty gap.
                          <Checkbox size="small" disabled />
                        )}
                      </StyledTableCell>
                    )}
                    {row.getVisibleCells().map((cell) => {
                      const meta = cell.column.columnDef
                        .meta as TableColumnMeta<TData, unknown>;
                      return (
                        <StyledTableCell
                          key={`${cell.id}-${cell.getValue()}`}
                          sx={{
                            ...getBreakpointSx(meta?.minimumBreakpoint),
                            ...getSizeSx(cell.column.columnDef.maxSize),
                          }}
                        >
                          {isEditModeEnabled &&
                            !!meta?.editor &&
                            flexRender(meta.editor, cell.getContext())}
                          {isEditModeEnabled &&
                            !meta?.editor &&
                            flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          {!isEditModeEnabled &&
                            flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                        </StyledTableCell>
                      );
                    })}
                  </TableRowComponent>
                  {renderSubComponent && (
                    <TableRow>
                      <StyledTableCell
                        colSpan={
                          row.getVisibleCells().length + (selectable ? 1 : 0)
                        }
                        // No cell padding: a collapsed panel then adds zero
                        // height (no stray gap under each row), and an expanded
                        // one spans the full row width/height — its own content
                        // owns the inner spacing.
                        sx={{
                          p: 0,
                          backgroundColor: 'action.selected',
                          borderBottom: row.getIsExpanded()
                            ? 'none'
                            : '1px solid rgba(224, 224, 224, 1)',
                        }}
                      >
                        {/*
                          `unmountOnExit` so a collapsed panel costs nothing: its
                          subtree isn't mounted, and — the reason it matters —
                          its inputs and buttons stay out of the tab order. A
                          table of rows whose panels each hold a form would
                          otherwise put every hidden control in the keyboard
                          path, and mount them all up front.
                        */}
                        <Collapse in={row.getIsExpanded()} unmountOnExit>
                          {renderSubComponent(row)}
                        </Collapse>
                      </StyledTableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
        </TableBody>
        {displayFooter && (
          <TableFooter>
            {table.getFooterGroups().map((footerGroup) => (
              <TableRow key={footerGroup.id}>
                {selectable && <StyledTableCell padding="checkbox" />}
                {footerGroup.headers.map((footer) => {
                  const meta = footer.column.columnDef.meta as TableColumnMeta<
                    TData,
                    unknown
                  >;
                  return (
                    <StyledTableCell
                      key={footer.id}
                      sx={{
                        fontWeight: 'bold',
                        ...getBreakpointSx(meta?.minimumBreakpoint),
                      }}
                    >
                      {footer.isPlaceholder
                        ? null
                        : flexRender(
                            footer.column.columnDef.footer,
                            footer.getContext(),
                          )}
                    </StyledTableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableFooter>
        )}
      </MuiTable>
    </TableContainer>
  );
}
function buildRowDataId<TData>(row: Row<TData>) {
  // @ts-ignore Typescript has issue with this
  return row.original._id ?? '';
}
